import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { apiBaseUrl } from '../config';
import axios from 'axios';
import { verificationAPI } from '../services/api';
import { useTurnstile } from '../hooks/useTurnstile';
import logo from '../assets/logo-main.png';
import './AuthPages.css';

const RESEND_COOLDOWN_SECS = 120;
const PASSWORD_COMPLEXITY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const FEATURES = [
  { title: 'Free to start', desc: 'Create your account and access foundational courses immediately.' },
  { title: 'Educational blog & insights', desc: 'Articles and concept breakdowns from experienced market educators.' },
  { title: 'Tools built for learning', desc: 'SIP calculator and risk-reward calculator to reinforce what you study.' },
];

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" width="13" height="13" fill="none">
    <path d="M4 10.5l3.2 3.2L16 5.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
    <path d="M1.5 10C3 7.5 6 4.5 10 4.5s7 3 8.5 5.5c-1.5 2.5-4.5 5.5-8.5 5.5S3 12.5 1.5 10z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
    <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 4.5c-4 0-7 3-8.5 5.5.7 1.2 1.8 2.6 3.3 3.7M16.5 13.7C18 12.6 18.8 11.2 19.5 10c-1.5-2.5-4.5-5.5-9.5-5.5-1 0-1.9.1-2.7.3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
);

const STEPS = ['Your Details', 'Verify Email', 'Complete Registration'];

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step,            setStep]           = useState(0);
  const [name,            setName]           = useState('');
  const [email,           setEmail]          = useState('');
  const [otpCode,         setOtpCode]        = useState('');
  const [verificationId,  setVerificationId] = useState(null);
  const [phone,           setPhone]          = useState('');
  const [password,        setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw,          setShowPw]         = useState(false);
  const [showCpw,         setShowCpw]        = useState(false);

  const [sending,   setSending]   = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [toast, setToast] = useState({ message: '', type: 'error' });
  const showToast = (message, type = 'error') => setToast({ message, type });

  const { getTurnstileToken, resetTurnstile, turnstileRef, turnstileStatus, isTurnstileConfigured } = useTurnstile();
  const turnstileBlocking = isTurnstileConfigured && turnstileStatus !== 'ready' && turnstileStatus !== 'error';

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) { showToast('Please enter your full name.'); return; }
    if (!isValidEmail(email)) { showToast('Please enter a valid email address.'); return; }
    setSending(true);
    let turnstileToken;
    try { turnstileToken = await getTurnstileToken(); } catch {
      showToast('Verification check failed. Please try again.'); setSending(false); resetTurnstile(); return;
    }
    try {
      const res = await verificationAPI.check({ type: 'email', identifier: email, name: name.trim(), event: 'signup', turnstileToken });
      if (res.data?.Status === 'Success') {
        setStep(1);
        setResendCooldown(RESEND_COOLDOWN_SECS);
      } else {
        showToast(res.data?.Message?.Message || 'Failed to send code. Please try again.');
      }
    } catch (err) {
      showToast(err.response?.data?.Message?.Message || err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally { setSending(false); resetTurnstile(); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) { showToast('Please enter the 6-digit code.'); return; }
    setVerifying(true);
    let turnstileToken;
    try { turnstileToken = await getTurnstileToken(); } catch {
      showToast('Verification check failed. Please try again.'); setVerifying(false); resetTurnstile(); return;
    }
    try {
      const res = await verificationAPI.verify({ type: 'email', identifier: email, code: otpCode, turnstileToken });
      if (res.data?.Status === 'Success') {
        setVerificationId(res.data?.Message?.VerificationId);
        setStep(2);
      } else {
        showToast(res.data?.Message?.Message || 'Invalid code. Please try again.');
        resetTurnstile();
      }
    } catch (err) {
      showToast(err.response?.data?.Message?.Message || err.response?.data?.message || 'Something went wrong. Please try again.');
      resetTurnstile();
    } finally { setVerifying(false); }
  };

  const handleResend = () => {
    setOtpCode('');
    handleSendOTP(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { showToast('Passwords do not match.'); return; }
    if (!PASSWORD_COMPLEXITY_REGEX.test(password)) {
      showToast('At least 8 characters, with uppercase, lowercase, a number, and a symbol.'); return;
    }
    setLoading(true);
    let turnstileToken;
    try { turnstileToken = await getTurnstileToken(); } catch {
      showToast('Verification check failed. Please try again.'); setLoading(false); resetTurnstile(); return;
    }
    try {
      const res = await axios.post(`${apiBaseUrl}/user/signup`, {
        name, email, password,
        phone: phone.trim() ? `+91${phone.trim()}` : undefined,
        verificationId, turnstileToken,
      });
      if (res.data.success) {
        showToast('Account created! Signing you in…', 'success');
        await login(res.data.token);
        navigate('/dashboard');
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create account. Please try again.');
      resetTurnstile();
    } finally { setLoading(false); }
  };

  const pwChecks = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'Uppercase letter',      ok: /[A-Z]/.test(password) },
    { label: 'Lowercase letter',      ok: /[a-z]/.test(password) },
    { label: 'Number',                ok: /\d/.test(password) },
    { label: 'Symbol (!@# etc.)',     ok: /[^A-Za-z0-9]/.test(password) },
  ];

  const year = new Date().getFullYear();

  return (
    <div className="auth-split">
      <Seo title="Create Account — InvFly" description="Create an InvFly account to enrol in stock market courses." noIndex />
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      {/* ── Brand pane ── */}
      <aside className="auth-split__brand">
        <div className="auth-split__brand-bg" aria-hidden="true" />

        <header className="auth-split__brand-header">
          <img src={logo} alt="InvFly" className="auth-split__brand-logo" />
        </header>

        <div className="auth-split__brand-body">
          <h1 className="auth-split__brand-title">
            Start learning smarter, today.
          </h1>
          <p className="auth-split__brand-sub">
            Join InvFly for structured, concept-first financial education — courses, tools, and blog insights focused on discipline over shortcuts.
          </p>
          <ul className="auth-split__features">
            {FEATURES.map((f) => (
              <li key={f.title} className="auth-split__feature">
                <span className="auth-split__feature-icon" aria-hidden="true"><CheckIcon /></span>
                <div>
                  <div className="auth-split__feature-title">{f.title}</div>
                  <div className="auth-split__feature-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <footer className="auth-split__brand-footer">
          <span>© {year} InvFly Technologies</span>
          <span className="auth-split__brand-dot" aria-hidden="true">•</span>
          <span>Free to join · no credit card required</span>
        </footer>
      </aside>

      {/* ── Form pane ── */}
      <main className="auth-split__form">
        <div className="auth-split__form-inner">

          <div className="auth-split__form-logo">
            <img src={logo} alt="InvFly" />
            <Link to="/" style={{ fontSize: '12px', color: 'var(--text-3)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
              Home
            </Link>
          </div>

          {/* Step indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24 }}>
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                    background: i <= step ? 'var(--accent)' : 'var(--surface-2)',
                    color: i <= step ? 'var(--bg)' : 'var(--text-3)',
                    border: i === step ? '2px solid var(--accent)' : '2px solid transparent',
                    transition: 'all 0.2s'
                  }}>
                    {i < step
                      ? <svg viewBox="0 0 20 20" width="11" height="11" fill="none"><path d="M4 10l3.5 3.5L16 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : i + 1}
                  </div>
                  <span style={{ fontSize: 11, color: i <= step ? 'var(--text)' : 'var(--text-3)', fontWeight: i === step ? 600 : 400, whiteSpace: 'nowrap' }}>
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: 1, background: i < step ? 'var(--accent)' : 'var(--border)', transition: 'background 0.3s', minWidth: 16 }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 0: Name + Email ── */}
          {step === 0 && (
            <>
              <span className="auth-split__eyebrow">Step 1 of 3</span>
              <h2 className="auth-split__title">Create Account</h2>
              <p className="auth-split__subtitle">Enter your name and email to get started. We'll send a verification code.</p>

              <form onSubmit={handleSendOTP}>
                <div className="fg">
                  <label className="fl" htmlFor="su-name">Full Name</label>
                  <input className="fi" id="su-name" type="text" value={name}
                    onChange={e => setName(e.target.value)} placeholder="Your full name"
                    required autoFocus autoComplete="name" />
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="su-email">Email Address</label>
                  <input className="fi" id="su-email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} placeholder="you@email.com"
                    required autoComplete="email" />
                </div>

                <div ref={turnstileRef} className="turnstile-wrapper" aria-hidden="true" />

                <button type="submit" className="fsub" disabled={sending || !name.trim() || !email || turnstileBlocking} style={{ marginTop: 8 }}>
                  {sending ? 'Sending code…' : 'Send Verification Code'}
                </button>
              </form>

              <div className="auth-split__switch" style={{ marginTop: 20 }}>
                Already have an account? <Link to="/signin">Sign In</Link>
              </div>

              <p className="auth-split__legal">
                By registering you agree to our{' '}
                <Link to="/termsofservice">Terms of Service</Link>{' '}and{' '}
                <Link to="/privacypolicy">Privacy Policy</Link>.
              </p>
            </>
          )}

          {/* ── Step 1: Verify OTP ── */}
          {step === 1 && (
            <>
              <span className="auth-split__eyebrow">Step 2 of 3</span>
              <h2 className="auth-split__title">Verify Your Email</h2>
              <p className="auth-split__subtitle">We sent a 6-digit code to <strong style={{ color: 'var(--text)' }}>{email}</strong>. Enter it below.</p>

              <form onSubmit={handleVerifyOTP}>
                <div className="fg">
                  <label className="fl" htmlFor="su-otp">Verification Code</label>
                  <input className="fi" id="su-otp" type="text" value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code" maxLength={6} inputMode="numeric" autoComplete="one-time-code"
                    required autoFocus style={{ letterSpacing: '0.18em', fontSize: 18, textAlign: 'center' }} />
                </div>

                <div ref={turnstileRef} className="turnstile-wrapper" aria-hidden="true" />

                <button type="submit" className="fsub" disabled={verifying || otpCode.length !== 6 || turnstileBlocking} style={{ marginTop: 8 }}>
                  {verifying ? 'Verifying…' : 'Verify Code'}
                </button>
              </form>

              <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
                Didn't receive it?{' '}
                <button type="button" onClick={handleResend}
                  disabled={resendCooldown > 0 || sending || turnstileBlocking}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 600, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', opacity: resendCooldown > 0 ? 0.5 : 1, padding: 0 }}>
                  {resendCooldown > 0
                    ? `Resend in ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')}`
                    : 'Resend code'}
                </button>
              </div>

              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <button type="button" onClick={() => { setStep(0); setOtpCode(''); setResendCooldown(0); }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-3)', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                  ← Change email
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: Password + Phone ── */}
          {step === 2 && (
            <>
              <span className="auth-split__eyebrow">Step 3 of 3</span>
              <h2 className="auth-split__title">Complete Registration</h2>
              <p className="auth-split__subtitle">Set a strong password and optionally add your phone number.</p>

              <form onSubmit={handleSubmit}>
                <div className="fg">
                  <label className="fl" htmlFor="su-phone">
                    Phone <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-3)' }}>(optional)</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                    <span style={{
                      display: 'flex', alignItems: 'center', padding: '0 12px',
                      background: 'var(--surface-2)', border: '1px solid var(--border)',
                      borderRight: 'none', borderRadius: '8px 0 0 8px',
                      fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap', userSelect: 'none'
                    }}>+91</span>
                    <input className="fi" id="su-phone" type="tel" value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="1234567890" autoComplete="tel"
                      maxLength={10} inputMode="numeric"
                      style={{ borderRadius: '0 8px 8px 0', flex: 1 }} />
                  </div>
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="su-password">Password</label>
                  <div style={{ position: 'relative' }}>
                    <input className="fi" id="su-password" type={showPw ? 'text' : 'password'}
                      value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="Create a strong password" required autoComplete="new-password"
                      autoFocus style={{ paddingRight: 42 }} />
                    <button type="button" onClick={() => setShowPw(s => !s)} aria-label={showPw ? 'Hide' : 'Show'}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 4 }}>
                      {showPw ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {password && (
                    <div className="pw-rules">
                      {pwChecks.map((c, i) => (
                        <div key={i} className={`pw-rule ${c.ok ? 'ok' : 'fail'}`}>{c.ok ? '✓' : '✗'} {c.label}</div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="su-confirmpw">Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input className="fi" id="su-confirmpw" type={showCpw ? 'text' : 'password'}
                      value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repeat password" required autoComplete="new-password"
                      style={{ paddingRight: 42 }} />
                    <button type="button" onClick={() => setShowCpw(s => !s)} aria-label={showCpw ? 'Hide' : 'Show'}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 4 }}>
                      {showCpw ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <div ref={turnstileRef} className="turnstile-wrapper" aria-hidden="true" />

                <button type="submit" className="fsub" disabled={loading} style={{ marginTop: 4 }}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </button>
              </form>

              <p className="auth-split__legal" style={{ marginTop: 16 }}>
                By registering you agree to our{' '}
                <Link to="/termsofservice">Terms of Service</Link>{' '}and{' '}
                <Link to="/privacypolicy">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Signup;
